import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Form, Button, FormControl, InputGroup, Modal, Table, Row, Col } from 'react-bootstrap';
import { FaTimes, FaCheck, FaPlus } from 'react-icons/fa';
import { getAllHarvests } from '../../services/Harvest/harvestService';
import PaginationComponent from '../Pagination/PaginationComponent';
import { formatDate } from '../Utils/utils';
import CreateHarvestModal from '../Harvest/CreateHarvestModal';

const HarvestInput = ({ value, onChange, label, isInvalid, userRoles, createdNew }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedHarvest, setSelectedHarvest] = useState(value || { id: null, description: '' });
  const [searchText, setSearchText] = useState(value?.description || '');
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [harvests, setHarvests] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [filterParams, setFilterParams] = useState({});
  const previousFilterParams = useRef({});
  const [shouldFetch, setShouldFetch] = useState(false);

  useEffect(() => {
    setSelectedHarvest(value || { id: null, description: '' });
    setSearchText(value?.description || '');
    setIsReadOnly(!!value?.id);
  }, [value]);

  const fetchHarvests = useCallback(async () => {
    if (!shouldFetch) return;
    try {
      const data = await getAllHarvests(filterParams, page - 1, pageSize);
      setHarvests(data.content);
      setTotalPages(data.totalPages);
    } catch (error) {
      console.error('Erro ao buscar safras:', error);
    }
  }, [shouldFetch, filterParams, page, pageSize]);

  useEffect(() => {
    if (shouldFetch) {
      fetchHarvests();
    }
  }, [page, pageSize, filterParams, fetchHarvests, shouldFetch]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const filter = { description: searchText };
      handleFilter(filter);
      setShowModal(true);
    }
  };

  const handleSelectHarvest = (harvest) => {
    setSelectedHarvest(harvest);
    setSearchText(harvest.description);
    setIsReadOnly(true);
    onChange({ description: harvest.description, id: harvest.id });
    setShowModal(false);
    setShouldFetch(false);
  };

  const handleClear = () => {
    setSelectedHarvest({ id: null, description: '' });
    setSearchText('');
    setIsReadOnly(false);
    onChange(null);
  };

  const handleCloseModal = () => {
    if (!selectedHarvest.id) {
      setSearchText('');
    }
    setShowModal(false);
    setShouldFetch(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShouldFetch(true);
  };

  const handleFilter = (filters) => {
    const filtersChanged = JSON.stringify(previousFilterParams.current) !== JSON.stringify(filters);
    if (filtersChanged) {
      setFilterParams(filters);
      setPage(1);
      previousFilterParams.current = filters;
      setShouldFetch(true);
    }
  };

  return (
    <>
      <div className="mb-3">
        {label && <Form.Label className="mb-1">{label}</Form.Label>}
        <InputGroup className={isInvalid ? 'is-invalid' : ''}>
          <FormControl
            placeholder="Selecione uma safra"
            value={searchText}
            isInvalid={isInvalid}
            readOnly={isReadOnly}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            autoComplete='off'
          />
          <Button variant="outline-secondary" onClick={handleClear} disabled={userRoles}>
            <FaTimes />
          </Button>
        </InputGroup>
        {isInvalid && <div className="invalid-feedback">{label} é obrigatória.</div>}
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Selecionar Safra</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ textAlign: 'center', display: createdNew ? 'block' : 'none' }} className="mb-3">
            <Button
              variant="primary"
              className="button-custom mt-0"
              onClick={(e) => {
                e.stopPropagation();
                setShowModal(false);
                setShowCreateModal(true);
              }}
            >
              <FaPlus className="me-2" />
              Novo
            </Button>
          </div>

          <Table striped bordered hover responsive="sm" className="mt-3">
            <thead>
              <tr>
                <th>Descrição</th>
                <th>Data de Início</th>
                <th style={{ width: '50px' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {harvests.map((harvest) => (
                <tr key={harvest.id}>
                  <td>{harvest.description}</td>
                  <td>{formatDate(harvest.startDate)}</td>
                  <td>
                    <Button
                      variant="outline-primary"
                      onClick={() => handleSelectHarvest(harvest)}
                      className="btn-primary button-custom d-flex align-items-center justify-content-center p-1"
                      style={{ borderRadius: '4px', padding: '5px 10px', minWidth: '40px', height: '30px' }}
                    >
                      <FaCheck style={{ color: 'inherit', opacity: 0.6 }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Row className="mt-3">
            <Col>
              <PaginationComponent
                totalPages={totalPages}
                currentPage={page}
                onPageChange={handlePageChange}
                totalElements={totalPages * pageSize}
                size={pageSize}
                pageNumber={page - 1}
                numberOfElements={harvests.length}
              />
            </Col>
          </Row>
        </Modal.Body>
      </Modal>
      <CreateHarvestModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onCreated={(created) => {
          setShowCreateModal(false);
          setSelectedHarvest(created);
          onChange(created);
        }}
      />
    </>
  );
};

export default HarvestInput;